import { Icon } from '@smartgift/frontend-toolkit';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { trackerManager } from '@hero/clients';
import { FLOW_CODES, RECOGNITION_CREATION_STEPS, TRACKER_EVENT } from '@hero/constants';
import { ROUTE_PATHS } from '@hero/routes';
import { useCreateRecognition } from '@hero/services';
import { useNewRecognitionStore, useNotification, useShoppingCart } from '@hero/store/hooks';
import { getNameFeatureFormData } from '@hero/utils';
import { getNameRule } from '@hero/validations';

function useRecognitionCreateModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { mutateAsync: createRecognition } = useCreateRecognition();
  const {
    updateCode: updateRecognitionCode,
    updateName: updateRecognitionName,
    updateProducts: updateRecognitionProducts,
    reset: resetRecognition
  } = useNewRecognitionStore();
  const { products, updateProducts: updateCartProducts } = useShoppingCart();

  const showRecognitionCreateModal = () => {
    trackerManager.emit(TRACKER_EVENT.serviceInitialized, {
      service: {
        type: t('newRecognition')
      }
    });

    showNotification({
      header: t('nameYourFeature', { name: t('oneTimeGift').toLocaleLowerCase() }),
      formData: getNameFeatureFormData({
        label: t('featureName', { featureType: t('oneTimeGift') })
      }),
      primaryButtonProps: {
        endIcon: 'next',
        children: t('next')
      },
      secondaryButtonProps: { children: t('cancel') },
      notificationImage: <Icon name="gift-box" size="xxl" />,
      onSubmit: async ({ formValues, validate }) => {
        const isValid = await validate([
          {
            rule: getNameRule(t('recognition')),
            value: formValues.name
          }
        ]);
        if (!isValid) return true;

        resetRecognition();
        updateRecognitionName(formValues.name);
        updateRecognitionProducts(products);
        const { code } = await createRecognition({
          name: formValues.name,
          products,
          meta: {
            creationStep: !products?.length
              ? RECOGNITION_CREATION_STEPS.newRecognitionGifts
              : RECOGNITION_CREATION_STEPS.newRecognitionContactType
          },
          flowCode: FLOW_CODES.standard
        });

        updateRecognitionCode(code);
        updateCartProducts([]);
        navigate(
          !products?.length
            ? ROUTE_PATHS.NEW_RECOGNITION_GIFTS
            : ROUTE_PATHS.NEW_RECOGNITION_CONTACT_TYPE
        );
      }
    });
  };

  return {
    showRecognitionCreateModal
  };
}

export { useRecognitionCreateModal };
